import { Grid, Typography } from '@quillbot/design-system';
import createStyles from '@mui/styles/createStyles';
import { makeStyles } from '@mui/styles';
import React from 'react';
import Link from 'next/link';

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      maxWidth: '885px',
      margin: '20px auto',
      textAlign: 'justify',
      [theme.breakpoints.down(1050)]: {
        padding: '0px 20px',
      },

      '& h1': {
        [theme.breakpoints.down(767)]: {
          fontSize: '23px',
        },
      },

      '& h2': {
        [theme.breakpoints.down(767)]: {
          fontSize: '23px',
          lineHeight: 'normal',
        },
      },

      '& h3': {
        fontSize: '24px',
        fontWeight: 'normal',
        paddingTop: '20px',
        marginBottom: '-5px',
        [theme.breakpoints.down(767)]: {
          fontSize: '20px',
        },
      },
    },

    topSection: {
      '& h1': {
        marginBottom: '20px',
        marginTop: '30px',
        [theme.breakpoints.down(767)]: {
          marginBottom: '20px',
        },
      },
      '& p': {
        lineHeight: '22px',
      },
      [theme.breakpoints.down(767)]: {
        paddingBottom: '0px',
      },
    },
    featureDescription: {
      paddingTop: '30px',
      [theme.breakpoints.down(767)]: {
        paddingTop: '0px',
        marginTop: '0px',
      },
    },

    ruleBoxBg: {
      backgroundImage: `url(./images/theme/${theme.palette.mode}/grammarChecker/ruleBoxBg.png)`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: '100% 96%',
      backgroundPosition: 'center 50px',

      [theme.breakpoints.down(767)]: {
        backgroundImage: `url(./images/theme/${theme.palette.mode}/grammarChecker/ruleBoxBgMobile.png)`,
        backgroundPosition: 'center 630px',
      },
    },
  })
);

function WordCounterExplanation() {
  const classes = useStyles();

  return (
    <>
      <div className={`${classes.container} container`}>
        <Grid container>
          <Grid item xs={12}>
            <div className={classes.topSection}>
              <Typography component="h1" variant="h4" className="pageTitle">
                Free Online Word and Character Count Tool
              </Typography>
              {/* <h1 className="pageTitle">Free Online Word and Character Count Tool</h1> */}
              <p>
                QuillBot’s Word Counter is a free, easy-to-use tool for tracking word and character
                counts in your writing.
              </p>
              <p>
                Our online word count tool displays data for both words and characters at the same
                time and allows you to easily toggle on/off whether you’d like spaces to be counted
                as characters. It also shows limits for social media platforms that have restricted
                character counts.
              </p>
            </div>
          </Grid>
        </Grid>
        <Grid container className={classes.featureDescription}>
          <Grid item xs={12}>
            <Typography component="h2" variant="h4" className="pageTitle">
              What are word counter and character counter?
            </Typography>
            {/* <h2 className="pageTitle">What are word counters and character counters?</h2> */}
          </Grid>

          <Grid container>
            <Grid item xs={12} className={classes.topSection}>
              <p>
                When you add your text to QuillBot’s Word Counter, our powerful platform instantly
                performs the mundane counting tasks for you, so you can see right where your word
                and character limits are sitting for your project.
              </p>
              <p>
                So, if you don’t know what the rules are for what counts as a character or when
                compound words count as one or two words, save yourself the searching, and let
                QuillBot be the word and character calculator.
              </p>
              <p>
                Trust QuillBot’s expertise to know that hyphenated words only count as one, that
                accented letters don’t count as two characters, and that, usually, spaces do count
                as a character.
              </p>

              <p>
                There’s no need to go crossed-eyed counting words or characters manually just to
                make sure you’re hitting your writing benchmarks or staying under harsh character
                limits. Your time is worth more than that—and so is your eyesight.
              </p>
            </Grid>
          </Grid>
        </Grid>
        <Grid container className={classes.featureDescription}>
          <Grid item xs={12}>
            <Typography component="h2" variant="h4" className="pageTitle">
              When to use a Word/Character Counter?
            </Typography>
            {/* <h2 className="pageTitle">When to use a Word/Character Counter</h2> */}
          </Grid>

          <Grid container>
            <Grid item xs={12} className={classes.topSection}>
              <p>
                Online word and character counting tools have a variety of uses, from helping you to
                check your daily writing goals, meet assignment requirements, and create viral
                social media posts.
              </p>
            </Grid>

            <Grid item xs={12}>
              <Typography component="h3" variant="h4" className="pageTitle">
                Who needs a word counter?
              </Typography>
              {/* <h3 className="pageTitle">Who needs a word counter?</h3> */}
            </Grid>

            <Grid container>
              <Grid item xs={12} className={classes.topSection}>
                <p>
                  When counting words, the objective is usually to meet a minimum or a ballpark
                  number, with a few exceptions. Below are some use cases where a word counter would
                  be needed:
                </p>
              </Grid>
              <Grid>
                <p>
                  <b>Students:</b> Often in school or university students will be asked to write a
                  250-word short answer to a question to show their knowledge or a 2,000-word essay.
                  <p>
                    Besides assignments, students applying for college and graduate school
                    admissions will usually have a target word count for their application essays.
                    Because of the large volume of applications each school receives, maximum word
                    count limitations are usually also imposed.
                  </p>
                  <p>
                    For example, a student may be asked for a ~1,000-word personal statement, but
                    the fine print will note a hard limit of 2,000 words.{' '}
                  </p>
                </p>
                <p>
                  <b>Writers:</b> Daily writing goals are important for the serious writer, even at
                  the hobby level. Those looking to make writing a habit or hone their skills often
                  choose to set a daily word count goal, like Neil Gaiman’s 50-words-per-day habit
                  that, slowly but surely, helped him pen <i>Coraline</i>.
                </p>
                <p>
                  <b>Job Seekers:</b> When crafting cover letters or completing case studies for job
                  applications, it is sometimes required to meet target word count. For example, an
                  applicant may be asked to write a 1,500-word report on a case study to show their
                  knowledge of the market.
                </p>
                <p>
                  <b>Students, Educators, and Researchers:</b> Research abstracts are one exception
                  where the word count has a strict maximum that is typically difficult to achieve,
                  since abstracts often need to summarize years of research into ~300 words.
                </p>
                <p>
                  Another exception where word count maximums are typically used is in grant and
                  scholarship writing. It can be hard for students, educators, and researchers to
                  outline a proposed project or describe their experiences and goals when they have
                  to stick to such strict word limits.
                </p>
                <p>
                  Using a word counter tool during the drafting process can help with keeping your
                  writing on track so that, when you begin editing, you aren’t trying to cut an
                  essay in half or add another 1,000 words.
                </p>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography component="h3" variant="h4" className="pageTitle">
                Who needs a character counter?
              </Typography>
              {/* <h3 className="pageTitle">Who needs a character counter?</h3> */}
            </Grid>
            <Grid container>
              <Grid item xs={12} className={classes.topSection}>
                <p>
                  If you need to check your character count online, odds are that you are worried
                  less about hitting a certain number of characters and more about staying under a
                  certain hard limit.
                </p>
                <p>Here are a few examples of when a character counter would be very helpful:</p>
              </Grid>
              <Grid>
                <p>
                  <b>Social Media Users:</b> Most social media platforms have some kind of character
                  limit. Twitter’s notoriously short limit of 280 characters is among the harshest
                  hard limits, while Facebook recommends ~250 characters to achieve maximum
                  engagement—though its actual hard character limit extends to tens of thousands of
                  characters.
                </p>
                <p>
                  <b>Digital Marketers:</b> Content creators make sure their work is found by
                  utilizing metadata, like meta titles and descriptions, to make sure their work
                  ranks for relevant keywords to be found by search engine users. Metadata and other
                  SEO parameters, including Google snippets, must follow the suggested character
                  counts used by search engines, if the content is going to be found or featured.
                  <p>
                    Digital marketers who run ads will also have to adhere to character limits when
                    running their campaigns.
                  </p>
                </p>
                <p>
                  <b>Job Seekers:</b> When completing online job applications, questionnaires, or
                  assessments, many online platforms will have character limits for the text input
                  boxes in which you write your responses or answers.
                </p>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography component="h3" variant="h4" className="pageTitle">
                Who needs both a word and a character counter?
              </Typography>
              {/* <h3 className="pageTitle">Who needs both a word and a character counter?</h3> */}
            </Grid>
            <Grid container>
              <Grid item xs={12} className={classes.topSection}>
                <p>
                  There are many more use cases for both word and character counters, since
                  assignments and forms will always be set up in different ways to meet various
                  goals.
                </p>
                <p>
                  In addition to each counter’s single use cases, there are a couple of prominent
                  use cases where a writer or small business owner would need to be able to use both
                  kinds of counters to complete a single project. Check them out below:
                </p>
              </Grid>
              <Grid>
                <p>
                  <b>Bloggers:</b> When writing a blog post, there are many tasks, like research,
                  drafting, and SEO, that must be done in order for an article to be indexed and
                  found online. For example, it is common practice for bloggers to aim for over
                  1,000 words in each article so that they include all of the necessary information
                  related to a topic.
                </p>
                <p>
                  On top of that word count goal, once an article has been drafted, the SEO details
                  (like meta title and description) must be crafted within certain character limits
                  so that search engines will know to provide the article as a result for relevant
                  keywords.
                </p>
                <p>
                  <b>Online Business Owners:</b> Those doing business online on their own website or
                  on a third-party platform often have a target word count for product descriptions
                  for consistency, and this is often mandated, usually with a minimum, for some
                  online retail platforms.
                  <p>
                    For businesses with their own site, SEO and the related character limits are
                    extremely important to follow so that search engines understand the business and
                    market to point searching users to the right product pages.
                  </p>
                  <p>
                    For sellers on a platform, most will still have to understand and optimize for
                    keywords and metadata in order for their products to be found correctly, and
                    these limits are almost always given in characters instead of words.
                  </p>
                </p>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container className={classes.featureDescription}>
          <Grid item xs={12}>
            <Typography component="h2" variant="h4" className="pageTitle">
              How to use the Word Counter?
            </Typography>
            {/* <h2 className="pageTitle">How to use the Word Counter?</h2> */}
          </Grid>

          <Grid container>
            <Grid item xs={12} className={classes.topSection}>
              <p>
                To use QuillBot’s free online word and character count tool, just type or paste in
                your text, and QuillBot’s Word Counter will instantly calculate the number of words
                and characters. It will even show you how your character counts stack up against
                limits for popular social media platforms.
              </p>
              <p>
                When you’re done working on a section of text, use the trash can icon in the top
                right corner to clear the window for a clean slate.
              </p>
              <p>
                If you’ve changed or refined the input text to meet a target word count or stay
                under a strict character limit, you can easily download it or copy the updated text
                to your clipboard using the icons in the bottom right corner.
              </p>
              {/**
               * Uncomment in next release after adding this feature.
               */}
              {/* <p>
                If you find yourself struggling to meet a certain word or character count, get some
                fresh ideas by moving your text over to{' '}
                <Link href="/">
                  <a target="_blank"> paraphraser</a>
                </Link>{' '}
                [using the button on the bottom right]. Using the drop-down thesaurus, you can see
                options for using different words or phrases that will help you meet or stay under
                your target.
              </p> */}
            </Grid>
          </Grid>
        </Grid>
        <Grid container className={classes.featureDescription}>
          <Grid item xs={12}>
            <Typography component="h2" variant="h4" className="pageTitle">
              How is QuillBot’s Word Counter different from other word counters online?
            </Typography>
            {/* <h2 className="pageTitle">
              How is QuillBot’s Word Counter different from other word counters online?
            </h2> */}
          </Grid>

          <Grid container>
            <Grid item xs={12} className={classes.topSection}>
              <p>
                It might be the newest word and character checker on the market, but it will soon
                become your favorite for at least one of the following reasons:
              </p>
              <ol type="1">
                <li>
                  <p>
                    <b>See everything you need (and more) at once.</b>
                    <p>
                      The word and character counters are displayed side-by-side, so whether you’re
                      reviewing one or both for your project, there’s no extra clicking, hovering,
                      or changing tabs to see one count or the other.{' '}
                    </p>
                    <p>
                      Plus, you can also see the character limit counters for optimized Facebook
                      posts (250 characters) and the hard limit for Twitter posts (280 characters)
                      displayed right on the page in the bottom right-hand corner.
                    </p>
                  </p>
                </li>
                <li>
                  <p>
                    <b>Enjoy zero clutter.</b>
                    <p>
                      QuillBot’s Word Counter has a clean, intuitive, and clutter-free interface.
                      You won’t be bothered with pop-ups or noisy ads, and it’s still free.
                    </p>
                  </p>
                </li>
                <li>
                  <p>
                    <b>It’s more than just a word and character counter.</b>
                    <p>
                      You might come to QuillBot for the free Word Counter, but once you’re here, we
                      bet you’ll stay because of all of the other productivity-enhancing features
                      that our platform offers.
                    </p>
                  </p>
                </li>
              </ol>
              <p>
                The QuillBot platform has a whole assortment of writing and research tools available
                for free, like...
                <ul>
                  <li>
                    A state-of-the-art{' '}
                    <Link href="/">
                      <a target="_blank">Paraphraser</a>
                    </Link>{' '}
                    to help you easily articulate your thoughts
                  </li>
                  <li>
                    A{' '}
                    <Link href="/grammar-check">
                      <a target="_blank">Grammar Checker</a>
                    </Link>{' '}
                    that is robust enough to catch errors in grammar, spelling, punctuation, word
                    use, formatting, plus mistakes related to English fluency
                  </li>
                  <li>
                    An instant text{' '}
                    <Link href="/summarize">
                      <a target="_blank"> Summarizer</a>
                    </Link>{' '}
                    to help you quickly review papers or articles, compare and contrast possible
                    sources, and check your reading comprehension
                  </li>
                  <li>
                    A frustration-free{' '}
                    <Link href="/citation-generator">
                      <a target="_blank">Citation Generator</a>
                    </Link>{' '}
                    that supports MLA, APA, and Chicago Style citation formats in one click
                  </li>
                  <li>
                    Plus the{' '}
                    <Link href="/co-writer">
                      <a target="_blank">Co-Writer word processing software</a>
                    </Link>
                    , which lets you complete your entire writing workflow without ever opening
                    another app—research, compose, refine, edit, cite, and check for writing
                    mistakes all in one place
                  </li>
                </ul>
              </p>
              <p>And that’s all in addition to the Word Counter.</p>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </>
  );
}

export default WordCounterExplanation;
